/**
 * Feature flags · compile-time vertical gating.
 * When we expand beyond freight, add slugs here.
 */
export const ENABLED_VERTICALS = ["freight", "logistics"] as const;

/**
 * When false, broker signup/landing/dashboard flows are hidden. Flipped to
 * true on 2026-05-04 alongside the broker-free pricing tier launch — broker
 * lookup is now the core wedge, so the homepage variant toggle, founding-10
 * cohort copy, and signup CTAs all surface broker-side flows.
 */
export const BROKER_FLOWS_ENABLED = true;

/** When false, the Business Vertical selector is hidden (freight-only). Flip to true for multi-vertical support. */
export const MULTI_VERTICAL_ENABLED = false;

/**
 * Hides the SMS delivery channel for partner watch alerts. Off until
 * (a) Twilio delivery is wired in carrier-monitor / partner-watch-diff and
 * (b) a CASL/CTIA consent flow records explicit opt-in for the phone number
 * on the user profile. Showing the toggle without those is a regulatory risk.
 */
export const PARTNER_ALERT_SMS_ENABLED = false;

export const FREIGHT_AGENT_TYPES = [
  "operations",
  "logistics",
  "broker",
  "carrier",
  "dispatcher",
  "freight_forwarder",
  "custom",
] as const;

export const FREIGHT_INDUSTRIES = [
  "Logistics",
  "Transportation",
  "Freight",
  "Supply Chain",
  "Warehousing",
  "3PL",
] as const;

export function isVerticalEnabled(slug: string): boolean {
  return (ENABLED_VERTICALS as readonly string[]).includes(slug);
}

// ── Feature flags for gated capabilities ──

export type FeatureFlag =
  | "fleet_dispatch"
  | "fleet_broadcast"
  | "sms_notifications"
  | "chrome_extension"
  | "embeddable_widget"
  | "inbound_webhooks"
  | "slack_notifications"
  | "broker_load_posting"
  | "broker_deal_engine"
  | "broker_carrier_search"
  | "rate_intelligence"
  | "double_broker_detect"
  | "load_board_aggregation"
  | "factoring_integration"
  | "hos_compliance"
  | "custom_agent_creation"
  | "agent_advanced_view";

type TierFlags = Record<FeatureFlag, boolean>;

const TIER_FEATURES: Record<string, TierFlags> = {
  free: {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  solo: {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  "broker-free": {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: true,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  pro: {
    fleet_dispatch: true,
    fleet_broadcast: false,
    sms_notifications: true,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: true,
  },
  fleet: {
    fleet_dispatch: true,
    fleet_broadcast: true,
    sms_notifications: true,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: true,
  },
  enterprise: {
    fleet_dispatch: true,
    fleet_broadcast: true,
    sms_notifications: true,
    chrome_extension: true,
    embeddable_widget: true,
    inbound_webhooks: true,
    slack_notifications: true,
    broker_load_posting: true,
    broker_deal_engine: true,
    broker_carrier_search: true,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: true,
    factoring_integration: true,
    hos_compliance: true,
    custom_agent_creation: true,
    agent_advanced_view: true,
  },
};

/**
 * Check if a feature is enabled for a given subscription tier.
 * Defaults to "free" tier if tier is unknown.
 */
export function isFeatureEnabled(tier: string | null | undefined, feature: FeatureFlag): boolean {
  const flags = TIER_FEATURES[tier ?? "free"] ?? TIER_FEATURES.free;
  return flags[feature] ?? false;
}

/**
 * Get all enabled features for a tier (useful for displaying feature lists).
 */
export function getEnabledFeatures(tier: string | null | undefined): FeatureFlag[] {
  const flags = TIER_FEATURES[tier ?? "free"] ?? TIER_FEATURES.free;
  return (Object.entries(flags) as [FeatureFlag, boolean][])
    .filter(([, enabled]) => enabled)
    .map(([flag]) => flag);
}

// ── Phase gating (Phase 1 rollout, Bruno pilot → June GA) ──
//
// Orthogonal to TIER_FEATURES above: tiers control what a paying customer unlocks,
// phases control what has been *productized* yet. A Phase 1 enterprise customer
// still cannot see Phase 2 features · they ship together when we flip the mode.

export type Phase = "phase1" | "phase2";

/** Feature keys that are Phase 2 only. Everything else is always available. */
export type PhaseFeature =
  | "load_board"
  | "load_posting"
  | "dispatch_hub"
  | "fleet_registry"
  | "deal_engine"
  | "trust_links"
  | "rate_intelligence"
  | "marketplace"
  | "deal_builder"
  | "batch_documents"
  | "drivers"
  | "procurement"
  | "chrome_extension"
  | "website_scanner"
  | "email_ai"
  | "automation_rules"
  | "broker_lookup"
  | "document_ai_batch"
  | "email_templates"
  | "invoices"
  | "documents"
  | "fleet_tracking"
  | "carrier_vetting_swipe"
  | "embed_badge";

// 2026-04-21 scope lock for Bruno pilot: verification-only wedge.
// Phase 1 CORE = carrier lookup (US + CA), broker lookup, public profile pages
// + claim flow, settings, home. EVERYTHING ELSE hidden until Bruno signs off,
// then drip-release per-tenant via companies.feature_overrides.
// broker_lookup is NOT in the hide set — it's the core demo.
const PHASE2_FEATURES = new Set<PhaseFeature>([
  "load_board",
  "load_posting",
  "dispatch_hub",
  "deal_engine",
  "rate_intelligence",
  "marketplace",
  "deal_builder",
  "procurement",
  "chrome_extension",
  "website_scanner",
  "email_ai",
  "automation_rules",
  "document_ai_batch",
  "trust_links",
  "batch_documents",
  "drivers",
  "fleet_registry",
  "email_templates",
  "invoices",
  "documents",
  "fleet_tracking",
  // carrier_vetting_swipe re-flagged 2026-05-05 to hide the swipe surface from
  // tenants by default. The original blank-page concern (no override → blank deck)
  // is resolved: CarrierVettingSwipePage now redirects to /dash/carriers/lookup
  // when the flag resolves false, and the SwipeViewToggle in the page only
  // renders when the flag is on. Admins still bypass via PhaseGate.allowAdmin.
  "carrier_vetting_swipe",
  // Embed-your-trust-badge surface on MyTrustScorePage. Backend route
  // `/v1/embed.js` (trust-score widget) shipped 2026-05-04 but no UI for
  // carriers to discover or copy the snippet. Flagged off so admins can
  // dogfood + verify before public exposure. Admin override via
  // AdminHubPage / companies.feature_overrides.embed_badge = true.
  "embed_badge",
]);

/**
 * Is a feature available in the given phase?
 * Phase 1 only surfaces verification + email + settings. Phase 2 opens everything else.
 *
 * DRIP-RELEASE WARNING (2026-05-02 hivesim red team finding):
 * Do NOT flip a tenant's `companies.settings.dashboard_mode` from `"audit"` to
 * something else to unlock features — that wholesale-unlocks every PHASE2_FEATURES
 * entry at once and blasts Bruno (or any pilot) with 22 half-built surfaces in
 * a single session. Instead, drip features per-tenant via
 * `companies.feature_overrides.<feature_key> = true` so each surface unlocks
 * deliberately. AdminHubPage.writeFeatureOverride is the supported path.
 */
export function isPhaseFeatureEnabled(phase: Phase, feature: PhaseFeature): boolean {
  if (phase === "phase2") return true;
  return !PHASE2_FEATURES.has(feature);
}

// ── Dash v2 rebuild flags (added 2026-04-10) ──
//
// Ground-up dashboard rebuild from Paper artboards. While v2 is being built, the
// old /dashboard routes all collapse to an Under Reconstruction page. The new
// dash lives under /dash until v2 is proven, then swaps to /dashboard.

/**
 * Master kill switch for the v2 dashboard. Defaults to false so the old dash
 * (now an Under Reconstruction page) keeps rendering for anyone not opted in.
 * Flip to true per-tenant via companies.feature_overrides, or globally once v2
 * is proven.
 */
export const DASH_V2_DEFAULT = false;

/**
 * Controls whether carriers can sign up without an invitation.
 * - "public":     anyone can sign up at /carriers/signup
 * - "invite_only": signup form replaced with "contact your dispatcher" message,
 *                  only trust-link invite tokens can create a carrier account
 *
 * Global default is "public". Tenants can override via admin hub (writes to
 * companies.feature_overrides.carrierSignupMode). The check is enforced server-side
 * in external-agent-register, NOT just frontend.
 */
export type CarrierSignupMode = "public" | "invite_only";
export const CARRIER_SIGNUP_MODE_DEFAULT: CarrierSignupMode = "public";

/**
 * Check if a tenant has dash v2 enabled. Reads from the company's feature_overrides
 * JSONB column (set via tenant-config edge function), falls back to global default.
 */
export function isDashV2Enabled(featureOverrides: Record<string, unknown> | null | undefined): boolean {
  if (featureOverrides && typeof featureOverrides.dashV2 === "boolean") {
    return featureOverrides.dashV2;
  }
  return DASH_V2_DEFAULT;
}

/**
 * Resolve carrier signup mode for a tenant (or global if no tenant context).
 */
export function resolveCarrierSignupMode(
  featureOverrides: Record<string, unknown> | null | undefined,
): CarrierSignupMode {
  const override = featureOverrides?.carrierSignupMode;
  if (override === "public" || override === "invite_only") {
    return override;
  }
  return CARRIER_SIGNUP_MODE_DEFAULT;
}

// ── CR Registry — public registry flags (added 2026-05-02) ──
//
// Phase 2 Wave 2 work. Default OFF in production (so the new public registry
// surface is invisible until we explicitly cut it over) and ON in development
// builds (`import.meta.env.MODE === 'development'`) so local builds always
// see the new pages without per-developer overrides. Each flag below maps to
// a specific surface so we can drip-release independently:
//
//   - public_registry      — /registry home + search results page
//   - registry_claim_flow  — claim-this-listing flow on detail pages
//   - registry_mcp_tools   — MCP tools that read the public registry
//   - registry_seo_pages   — auto-generated SEO landing pages per identifier
//
// All four are cohesive: ship the home + claim + MCP + SEO together when GA.

export type RegistryFeatureFlag =
  | "public_registry"
  | "registry_claim_flow"
  | "registry_mcp_tools"
  | "registry_seo_pages";

const REGISTRY_FLAG_DEFAULTS_DEV: Record<RegistryFeatureFlag, boolean> = {
  public_registry: true,
  registry_claim_flow: true,
  registry_mcp_tools: true,
  registry_seo_pages: true,
};

const REGISTRY_FLAG_DEFAULTS_PROD: Record<RegistryFeatureFlag, boolean> = {
  public_registry: true,
  registry_claim_flow: true,
  registry_mcp_tools: true,
  registry_seo_pages: true,
};

/**
 * Read the current value of a registry feature flag.
 * Defaults vary by build mode: ON in dev, OFF in prod. We read
 * `import.meta.env.MODE` (Vite) the same way `src/lib/sentry.ts` does.
 */
export function isRegistryFeatureEnabled(flag: RegistryFeatureFlag): boolean {
  const isDev = import.meta.env.MODE === "development";
  const defaults = isDev ? REGISTRY_FLAG_DEFAULTS_DEV : REGISTRY_FLAG_DEFAULTS_PROD;
  return defaults[flag] ?? false;
}

/** Convenience accessor used by route gates and feature surfaces. */
export const registryFeatures = {
  get public_registry() {
    return isRegistryFeatureEnabled("public_registry");
  },
  get registry_claim_flow() {
    return isRegistryFeatureEnabled("registry_claim_flow");
  },
  get registry_mcp_tools() {
    return isRegistryFeatureEnabled("registry_mcp_tools");
  },
  get registry_seo_pages() {
    return isRegistryFeatureEnabled("registry_seo_pages");
  },
} as const;
